import type { ScheduleData } from "@/features/Assistant/types/schedule";
import { get, post, del } from "@/shared/utils/fetchApi";
import {
  createAuthenticatedSSE,
  type SSESubscription,
} from "@/shared/utils/fetchSSE";
import { API_CONFIG } from "@/infrastructure/config/api";


export class ScheduleService {
  static async getSchedule(): Promise<ScheduleData> {
    return get<ScheduleData>("/assistant/schedule");
  }

  static createScheduleStream(
    onUpdate: (data: ScheduleData) => void,
    onError?: (event: Event) => void,
  ): SSESubscription {
    const url = `${API_CONFIG.API_ROOT_URL}/assistant/schedule/stream`;

    return createAuthenticatedSSE(url, {
      onMessage: (data) => {
        if (!data || data === ":") return;
        try {
          const parsed = JSON.parse(data);
          // Only valid snapshots (which always carry `summary`) update the screen.
          if (parsed && typeof parsed === "object" && "summary" in parsed) {
            onUpdate(parsed as ScheduleData);
          } else if (parsed && parsed.error && onError) {
            // Server signalled a snapshot build failure — surface it instead of going stale.
            onError(
              new ErrorEvent("error", {
                error: new Error(String(parsed.error)),
              }),
            );
          }
        } catch {
          // Non-JSON SSE payload (e.g. keep-alive) — safe to ignore
        }
      },
      onError: (error) => {
        if (onError) {
          onError(new ErrorEvent("error", { error }));
        }
      },
    });
  }

  static async cancelMeeting(id: string): Promise<void> {
    await del(`/assistant/schedule/meetings/${id}`);
  }

  /** Stops the bot from attending a whole series, whatever the calendar says. */
  static async blockSeries(seriesId: string): Promise<void> {
    await del(`/assistant/schedule/series/${seriesId}`);
  }

  static async forceLeaveCall(callId: string): Promise<void> {
    await del(`/assistant/schedule/calls/${callId}`);
  }

  /** Brings the next calendar read forward instead of waiting for the cycle. */
  static async syncNow(): Promise<void> {
    await post("/assistant/schedule/sync");
  }

  static formatTime(isoString: string, locale?: string): string {
    if (!isoString) return "-";
    const date = new Date(isoString);
    const userLocale = locale || navigator.language || "pt-BR";
    return date.toLocaleString(userLocale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  static formatTimeRange(start: string, end?: string, locale?: string): string {
    if (!start) return "-";
    const userLocale = locale || navigator.language || "pt-BR";
    const startDate = new Date(start);
    const startFormatted = startDate.toLocaleString(userLocale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    if (!end) return startFormatted;

    const endDate = new Date(end);
    const sameDay =
      startDate.toLocaleDateString(userLocale) ===
      endDate.toLocaleDateString(userLocale);

    if (sameDay) {
      const endTime = endDate.toLocaleTimeString(userLocale, {
        hour: "2-digit",
        minute: "2-digit",
      });
      return `${startFormatted} — ${endTime}`;
    }

    return `${startFormatted} — ${this.formatTime(end, locale)}`;
  }

  static formatTimeOnly(isoString: string, locale?: string): string {
    if (!isoString) return "-";
    const date = new Date(isoString);
    const userLocale = locale || navigator.language || "pt-BR";
    return date.toLocaleTimeString(userLocale, {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  static formatTimeOnlyRange(
    start: string,
    end?: string,
    locale?: string,
  ): string {
    if (!start) return "-";
    const startTime = this.formatTimeOnly(start, locale);
    if (!end) return startTime;
    const endTime = this.formatTimeOnly(end, locale);
    return `${startTime} — ${endTime}`;
  }
}
